// Frontend entry point. Loads data, sets up the router, switches between
// tabs in the dock, and pins the inspector into the slide-over panel.
// Intentionally vanilla TS — no framework — because the viz functions are
// imperative D3 anyway and a small router + a few render() calls is less
// code than a React/Svelte equivalent.

import { renderClusterMap } from "./viz/cluster-map.ts";
import { renderTrendMatrix } from "./viz/matrix.ts";
import { renderCloud } from "./viz/cloud.ts";
import { renderTopicInspector } from "./viz/inspector.ts";
import { renderCockpit } from "./views/cockpit.ts";
import { renderTopicPage } from "./views/topic-page.ts";
import { renderMemos } from "./views/memos.ts";
import { renderSandbox } from "./views/sandbox.ts";
import { renderCapture } from "./views/capture.ts";
import { renderConsole } from "./views/console.ts";
import { renderAdmin } from "./views/admin.ts";
import { renderMapFilters } from "./views/map-filters.ts";
import { applyFilters, loadInitial as loadInitialFilters, persist as persistFilters, type MapFilters } from "./lib/filters.ts";
import { copyShareLink, isShared } from "./lib/share.ts";
import { composeTopic, loadGraph } from "./lib/queries.ts";
import { getSession, onAuthChange, signInWithEmail, signOut } from "./lib/auth.ts";
import { ingestUrl } from "./lib/actions.ts";
import { navigate, onRouteChange, parse, type Tab } from "./lib/router.ts";
import type { TopicWithContext } from "./lib/types.ts";

// ---------- DOM helpers ----------
const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T | null;
const setText = (id: string, t: string) => { const el = $(id); if (el) el.textContent = t; };

function setStatus(text: string, kind: "loading" | "ok" | "error" = "ok") {
  setText("statusText", text);
  const dot = $<HTMLElement>("statusDot");
  if (dot) {
    dot.classList.remove("status-loading", "status-ok", "status-error");
    dot.classList.add(`status-${kind}`);
  }
}

// Pretty title for each tab. Drives the content header.
const TAB_TITLE: Record<Tab, string> = {
  cockpit: "Cockpit",
  cluster: "Map",
  matrix:  "Quadrant",
  cloud:   "Cloud",
  memos:   "Memos",
  sandbox: "Sandbox",
  capture: "Capture",
  console: "Console",
  inbox:   "Console",
  ops:     "Console",
  admin:   "Admin",
  topic:   "Topic",
};

// ---------- App state ----------
interface State {
  graph: Awaited<ReturnType<typeof loadGraph>> | null;
  pinnedTopic: TopicWithContext | null;
  destructors: Record<Tab, (() => void) | null>;
  filterDestructors: Record<"cluster" | "matrix" | "cloud", (() => void) | null>;
  filters: MapFilters;
}
const state: State = {
  graph: null,
  pinnedTopic: null,
  destructors: { cockpit: null, cluster: null, matrix: null, cloud: null, memos: null, sandbox: null, capture: null, console: null, inbox: null, ops: null, admin: null, topic: null },
  filterDestructors: { cluster: null, matrix: null, cloud: null },
  filters: loadInitialFilters(),
};

// Current period scope (W/M/Q/Y/ALL). Hoisted so switchTab() can read it
// before the listener below initializes.
let currentPeriod: "W" | "M" | "Q" | "Y" | "ALL" = "M";

// ---------- Slide-over inspector ----------
function openSlideover() {
  $("slideover")?.classList.add("open");
}
function closeSlideover() {
  $("slideover")?.classList.remove("open");
}
function pinTopic(topicId: number) {
  if (!state.graph) return;
  const ctx = composeTopic(topicId, state.graph);
  if (!ctx) return;
  state.pinnedTopic = ctx;
  const body = $("railInspectorBody")!;
  renderTopicInspector(body, ctx, state.graph.metrics, state.graph.businessLines, {
    onUnpin: () => unpinTopic(),
  });
  setText("slideoverMeta", ctx.topic.slug ?? "");
  openSlideover();
}
function unpinTopic() {
  state.pinnedTopic = null;
  const body = $("railInspectorBody")!;
  body.innerHTML = `
    <div class="slideover-empty">
      <p>Pin a topic from the map, quadrant, or cloud to see its rationale here.</p>
    </div>
  `;
  setText("slideoverMeta", "");
  closeSlideover();
}

// ---------- Map tab rendering (shared filter chrome) ----------
// Shared by cluster / matrix / cloud. Reads state.graph + state.filters,
// applies the filter, mounts the filter bar above the canvas, then defers
// to the per-lens renderer with the filtered data.
function renderMapTab(lens: "cluster" | "matrix" | "cloud") {
  if (!state.graph) return;
  const allCtxs = state.graph.topics.map((t) => composeTopic(t.id, state.graph!)!).filter(Boolean);
  const fullSlice = {
    categories: state.graph.categories,
    trends: state.graph.trends,
    topics: state.graph.topics,
    topicTrends: state.graph.topicTrends,
  };
  const { slice, ctxs, matchedCount } = applyFilters(
    fullSlice,
    allCtxs,
    state.graph.businessLines,
    state.filters,
  );

  // Tear down + remount the filter bar.
  state.filterDestructors[lens]?.();
  const filterHostId = `${lens}-filters`;
  const filterHost = $(filterHostId);
  if (filterHost) {
    state.filterDestructors[lens] = renderMapFilters(
      filterHost,
      state.filters,
      (next) => {
        state.filters = next;
        persistFilters(next);
        renderMapTab(lens);
      },
      { tab: lens, matchedCount, totalCount: allCtxs.length },
    );
  }

  // Tear down the previous viz destructor.
  state.destructors[lens]?.();

  if (lens === "cluster") {
    state.destructors.cluster = renderClusterMap(
      $("cluster-container")!,
      slice,
      { onTopicClick: pinTopic },
    );
    setText("contentMeta", `${slice.categories.length} categories · ${slice.trends.length} trends · ${slice.topics.length} topics`);
  } else if (lens === "matrix") {
    state.destructors.matrix = renderTrendMatrix($("matrix-container")!, ctxs, { onTopicClick: pinTopic });
    setText("contentMeta", `${matchedCount} topics plotted`);
  } else {
    state.destructors.cloud = renderCloud($("cloud-container")!, ctxs, {
      onWordClick: (_w, ids) => { if (ids[0]) pinTopic(ids[0]); },
    });
    setText("contentMeta", `${matchedCount} topics`);
  }
}

// ---------- Tab switching ----------
function switchTab(tab: Tab, slug?: string) {
  // Hide all panes; show the target.
  document.querySelectorAll<HTMLElement>(".surface-pane").forEach((el) => el.classList.add("hidden"));
  document.querySelectorAll<HTMLElement>(".dock-item[data-tab]").forEach((el) => el.classList.remove("active"));
  // Legacy aliases inbox/ops use the console pane.
  const paneTab: Tab = tab === "inbox" || tab === "ops" ? "console" : tab;
  $(`pane${paneTab[0].toUpperCase() + paneTab.slice(1)}`)?.classList.remove("hidden");
  // Topic deep page isn't a dock item; light up Map dock as the closest sibling.
  // inbox/ops are legacy aliases that route to console.
  let dockTab: Tab = tab;
  if (tab === "topic") dockTab = "cluster";
  if (tab === "inbox" || tab === "ops") dockTab = "console";
  document.querySelector<HTMLElement>(`.dock-item[data-tab="${dockTab}"]`)?.classList.add("active");

  // Header title.
  setText("contentTitle", TAB_TITLE[tab] ?? "Trend Tracker");

  // Shared-mode banner — visible only when ?share=1 is on the hash. The link
  // recipient sees this; the original author doesn't (their copy added the
  // marker to the URL they sent, not to their own browser hash).
  const sharedBanner = $("sharedBanner");
  if (sharedBanner) sharedBanner.classList.toggle("hidden", !isShared());

  // Tear down old destructor.
  const prev = state.destructors[tab];
  if (prev) prev();
  state.destructors[tab] = null;

  if (!state.graph) return;

  if (tab === "cockpit") {
    const dest = renderCockpit($("cockpit-container")!, currentPeriod, pinTopic);
    state.destructors.cockpit = dest;
    setText("contentMeta", `${state.graph.topics.length} topics · period · ${currentPeriod}`);
  } else if (tab === "cluster") {
    renderMapTab("cluster");
  } else if (tab === "topic") {
    const t = slug ? state.graph.topics.find((tp) => tp.slug === slug) : null;
    if (!t) {
      $("topic-container")!.innerHTML = `<div class="viz-empty">Topic not found: ${slug ?? ""}</div>`;
      setText("contentMeta", "");
    } else {
      const ctx = composeTopic(t.id, state.graph)!;
      const dest = renderTopicPage(
        $("topic-container")!,
        ctx,
        state.graph.metrics,
        state.graph.businessLines,
        { onClose: () => navigate("cluster") },
      );
      state.destructors.topic = dest;
      setText("contentTitle", t.name);
      setText("contentMeta", t.h3_thesis_flag ? "h3 thesis" : "topic");
    }
  } else if (tab === "matrix") {
    renderMapTab("matrix");
  } else if (tab === "cloud") {
    renderMapTab("cloud");
  } else if (tab === "memos") {
    state.destructors.memos = renderMemos($("memos-container")!, currentPeriod, pinTopic);
    setText("contentMeta", `period · ${currentPeriod}`);
  } else if (tab === "sandbox") {
    state.destructors.sandbox = renderSandbox($("sandbox-container")!);
    setText("contentMeta", "Bayesian scenario engine · slide to explore");
  } else if (tab === "capture") {
    state.destructors.capture = renderCapture($("capture-container")!);
    setText("contentMeta", "front door for new evidence");
  } else if (tab === "console" || tab === "inbox" || tab === "ops") {
    state.destructors.console = renderConsole($("console-container")!, currentPeriod);
    setText("contentMeta", `period · ${currentPeriod}`);
  } else if (tab === "admin") {
    setText("contentMeta", "");
    getSession().then((s) =>
      renderAdmin($("admin-container")!, s.role).then((dest) => {
        state.destructors.admin = dest;
      }),
    ).catch((e) => {
      $("admin-container")!.innerHTML = `<div class="viz-empty">Admin load failed: ${String(e)}</div>`;
    });
  }
}

// ---------- Auth UI ----------
async function refreshAuthUI() {
  const session = await getSession();
  const btn = $<HTMLButtonElement>("btnSignIn");
  if (!btn) return;
  const initialsEl = btn.querySelector<HTMLElement>(".dock-avatar-initials");

  if (session.email) {
    const display = session.display_name ?? session.email;
    const initials = display.slice(0, 2).toUpperCase();
    if (initialsEl) initialsEl.textContent = initials;
    btn.title = `${display} (${session.role}) — click to sign out`;
    btn.onclick = async () => {
      await signOut();
      refreshAuthUI();
    };
  } else {
    if (initialsEl) initialsEl.textContent = "·";
    btn.title = "Sign in";
    btn.onclick = async () => {
      const email = prompt("Email for magic-link sign-in:");
      if (!email) return;
      try {
        await signInWithEmail(email.trim());
        alert("Magic link sent. Check your email.");
      } catch (e) {
        alert(`Sign-in failed: ${String(e)}`);
      }
    };
  }
}

// ---------- Boot ----------
async function boot() {
  setStatus("Loading graph…", "loading");

  // Wire up the dock nav and router.
  document.querySelectorAll<HTMLButtonElement>(".dock-item[data-tab]").forEach((b) => {
    b.addEventListener("click", () => navigate(b.dataset.tab as Tab));
  });
  // Slide-over close handlers.
  $("slideoverClose")?.addEventListener("click", () => closeSlideover());

  // Share button — copies the current hash with a ?share=1 marker.
  $("btnShareCurrent")?.addEventListener("click", (e) => {
    const hashPath = location.hash.replace(/^#/, "") || "/cockpit";
    // Strip an existing share=1 to avoid duplication.
    const cleaned = hashPath.replace(/[?&]share=1/g, "").replace(/\?$/, "");
    copyShareLink(cleaned, e.currentTarget as HTMLButtonElement);
  });
  $("slideoverScrim")?.addEventListener("click", () => closeSlideover());
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") closeSlideover();
  });

  onRouteChange((r) => {
    // If hash carries filter params (e.g. cockpit drill-in), pick them up.
    if (r.tab === "cluster" || r.tab === "matrix" || r.tab === "cloud") {
      const fromHash = loadInitialFilters();
      // Only overwrite when the hash actually carries something — otherwise
      // we'd clobber filters the user set on a different lens.
      if (location.hash.includes("?")) state.filters = fromHash;
    }
    switchTab(r.tab, r.topicSlug ?? undefined);
  });
  onAuthChange(() => refreshAuthUI());

  try {
    state.graph = await loadGraph();
    setStatus(
      `${state.graph.topics.length} topics · ${state.graph.metrics.length} metrics · ${state.graph.businessLines.length} business lines`,
      "ok",
    );

    const totalTopics = state.graph.topics.length;
    const h3Count = state.graph.topics.filter((t) => t.h3_thesis_flag).length;
    const curatedCount = state.graph.topics.filter((t) => !t.ai_drafted).length;
    const heroEl = $("heroStats");
    if (heroEl) {
      heroEl.innerHTML = `
        <div class="hero-stat"><span class="hero-stat-value">${totalTopics}</span><span class="hero-stat-label">topics tracked</span></div>
        <div class="hero-stat"><span class="hero-stat-value">${h3Count}</span><span class="hero-stat-label">h3 thesis</span></div>
        <div class="hero-stat"><span class="hero-stat-value">${curatedCount}</span><span class="hero-stat-label">curated</span></div>
      `;
    }
  } catch (e) {
    setStatus(`Load failed: ${String(e)}`, "error");
    return;
  }

  // Initial route → render.
  const r = parse();
  switchTab(r.tab, r.topicSlug ?? undefined);
  refreshAuthUI();
}

// Period selector. The W/M/Q/Y/ALL switch filters inbox + ops by created_at;
// the latest-score views (cluster/matrix/cloud) ignore it because they're
// always "latest". `currentPeriod` is hoisted at the top of this file.
document.addEventListener("click", (e) => {
  const target = (e.target as HTMLElement).closest<HTMLButtonElement>("#segPeriod button");
  if (!target) return;
  document.querySelectorAll("#segPeriod button").forEach((b) => b.classList.remove("active"));
  target.classList.add("active");
  currentPeriod = (target.dataset.period as typeof currentPeriod) ?? "M";
  // Re-render the active tab so inbox/ops pick up the new scope.
  const r = parse();
  switchTab(r.tab, r.topicSlug ?? undefined);
});

// URL-ingest button. Sign-in required; we surface a one-line result back to
// the user via the status bar so the action feels finished.
$("btnIngestUrl")?.addEventListener("click", async () => {
  const sess = await getSession();
  if (!sess.email) {
    alert("Sign in first — URL ingest stages a pending change attributed to you.");
    return;
  }
  const url = prompt("Paste a URL to file evidence against a topic:");
  if (!url) return;
  setStatus("Fetching + matching against topics…", "loading");
  try {
    const r = await ingestUrl(url.trim());
    setStatus(r.message ?? "Staged.", "ok");
    if (r.proposed_change_id) {
      navigate("inbox");
    }
  } catch (e) {
    setStatus(`Ingest failed: ${String(e)}`, "error");
  }
});

// Start.
boot().catch((e) => {
  setStatus(`Boot failed: ${String(e)}`, "error");
  // eslint-disable-next-line no-console
  console.error(e);
});
